import loginImage from '../../assets/claraview-v2/login-image.webp';
import signupImage from '../../assets/claraview-v2/signup-image.webp';
import signupLogo from '../../assets/claraview-v2/signup-logo.webp';

/*
 * Shared shell for every /login-family page: background photo, scrim, desktop-only left panel,
 * and the glass sign-in panel on the right. Two variants:
 *
 *   signin — 460px panel, tagline in the left panel  (Login, ForgotPassword, ResetPassword, NewPassword)
 *   signup — 480px panel, "What's Included" pills    (SignUp, OtpVerification)
 *
 * The `cv2-auth-*` classes below replace six near-identical per-page blocks (cv2-login-*,
 * cv2-signup-*, cv2-otp-*, cv2-forgot-*, cv2-reset-*, cv2-newpw-*). Tailwind's opacity modifier
 * doesn't work on our var(--cv2-*)-based colors, so every tinted value goes through color-mix()
 * against the cv2-foreground/background/destructive tokens rather than a hardcoded hex.
 */

const INCLUDED_ITEMS = [
    "AI Simulations",
    "Patient Dashboard",
    "In-App Messaging",
    "Email Templates",
    "Custom Sim Link",
    "Coaching & Mentorship",
];

const SHARED_STYLES = `
  .cv2-auth-scrim {
    background-image:
      linear-gradient(0deg, color-mix(in srgb, var(--cv2-background) 60%, transparent) 0%, color-mix(in srgb, var(--cv2-background) 45%, transparent) 25%, color-mix(in srgb, var(--cv2-background) 30%, transparent) 50%, color-mix(in srgb, var(--cv2-background) 15%, transparent) 75%, transparent 100%),
      linear-gradient(90deg, color-mix(in srgb, var(--cv2-background) 25%, transparent) 0%, color-mix(in srgb, var(--cv2-background) 50%, transparent) 50%, color-mix(in srgb, var(--cv2-background) 92%, transparent) 100%);
  }
  .cv2-auth-scrim-strong {
    background-image:
      linear-gradient(0deg, color-mix(in srgb, var(--cv2-background) 60%, transparent) 0%, color-mix(in srgb, var(--cv2-background) 45%, transparent) 25%, color-mix(in srgb, var(--cv2-background) 30%, transparent) 50%, color-mix(in srgb, var(--cv2-background) 15%, transparent) 75%, transparent 100%),
      linear-gradient(90deg, color-mix(in srgb, var(--cv2-background) 35%, transparent) 0%, color-mix(in srgb, var(--cv2-background) 55%, transparent) 50%, color-mix(in srgb, var(--cv2-background) 92%, transparent) 100%);
  }
  .cv2-auth-panel {
    background-color: color-mix(in srgb, var(--cv2-background) 72%, transparent);
    border-left-color: color-mix(in srgb, var(--cv2-foreground) 5%, transparent);
  }
  .cv2-auth-tagline-mark { color: color-mix(in srgb, var(--cv2-foreground) 40%, transparent); }
  .cv2-auth-tagline-text { color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent); }
  .cv2-auth-eyebrow { color: color-mix(in srgb, var(--cv2-foreground) 35%, transparent); }
  .cv2-auth-eyebrow-pill { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); }
  .cv2-auth-pill { border-color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent); }
  .cv2-auth-subcopy { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); }
  .cv2-auth-subcopy-strong { color: color-mix(in srgb, var(--cv2-foreground) 55%, transparent); }
  .cv2-auth-row { border-color: color-mix(in srgb, var(--cv2-foreground) 7%, transparent); }
  .cv2-auth-label { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-auth-input::placeholder { color: color-mix(in srgb, var(--cv2-foreground) 18%, transparent); }
  .cv2-auth-hint { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-auth-toggle { opacity: 0.3; transition: opacity 0.15s ease; }
  .cv2-auth-toggle:hover { opacity: 0.6; }
  .cv2-auth-link { color: color-mix(in srgb, var(--cv2-foreground) 25%, transparent); transition: color 0.15s ease; }
  .cv2-auth-link:hover { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); }
  .cv2-auth-footer { color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); transition: color 0.15s ease; }
  .cv2-auth-footer:hover { color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent); }
  .cv2-auth-resend { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); transition: color 0.15s ease; }
  .cv2-auth-resend:hover { color: color-mix(in srgb, var(--cv2-foreground) 65%, transparent); }
  .cv2-auth-resend:disabled { color: color-mix(in srgb, var(--cv2-foreground) 18%, transparent); }
  .cv2-auth-divider { border-color: color-mix(in srgb, var(--cv2-foreground) 6%, transparent); }
  .cv2-auth-join-copy { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-auth-join-button {
    border-color: color-mix(in srgb, var(--cv2-foreground) 12%, transparent);
    color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent);
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .cv2-auth-error-banner {
    color: var(--cv2-destructive);
    background-color: color-mix(in srgb, var(--cv2-destructive) 10%, transparent);
    border-color: color-mix(in srgb, var(--cv2-destructive) 30%, transparent);
  }
`;

const VARIANTS = {
    signin: {
        image: loginImage,
        scrimClass: 'cv2-auth-scrim',
        leftStyle: undefined,
        logoClass: 'h-8',
        panelClass: 'md:max-w-[460px] md:px-[52px] py-[52px]',
        mobileLogoWrap: 'mb-12',
        mobileLogoClass: 'h-[22px]',
    },
    signup: {
        image: signupImage,
        scrimClass: 'cv2-auth-scrim-strong',
        leftStyle: { maxWidth: 'calc(100% - 480px)' },
        logoClass: 'h-5',
        panelClass: 'md:w-[480px] md:max-w-[480px] shrink-0 md:pl-[53px] md:pr-[52px] py-12',
        mobileLogoWrap: 'mb-10',
        mobileLogoClass: 'h-5',
    },
};

const AuthLayout = ({ variant = 'signin', children }) => {
    const v = VARIANTS[variant];

    return (
        <div className="cv2-dark relative min-h-dvh flex overflow-hidden">
            <style>{SHARED_STYLES}</style>

            <img src={v.image} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
            <div className={`${v.scrimClass} absolute inset-0`} />

            {/* Left panel — desktop only */}
            <div
                className="relative hidden md:flex md:flex-col md:justify-between flex-1 p-10"
                style={v.leftStyle}
            >
                <img src={signupLogo} alt="Claraview" className={`${v.logoClass} w-auto object-contain object-left opacity-90`} />

                {variant === 'signin' ? (
                    <div className="flex items-start gap-[10px]">
                        <span className="cv2-auth-tagline-mark font-spaceMono text-sm mt-[3px] shrink-0">+</span>
                        <p className="cv2-auth-tagline-text font-aeonik text-lg leading-7 max-w-[280px] normal-case">
                            AI Smile Simulation and Cosmetic Case Growth Platform
                        </p>
                    </div>
                ) : (
                    <div className="pb-2">
                        <p className="cv2-auth-eyebrow-pill font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-4">
                            What's Included
                        </p>
                        <div className="flex flex-wrap gap-2 max-w-[420px]">
                            {INCLUDED_ITEMS.map((item) => (
                                <div
                                    key={item}
                                    className="cv2-auth-pill border rounded-full px-3.5 py-[7px] flex items-center justify-center"
                                >
                                    <span className="font-aeonik text-xs text-cv2-foreground leading-none text-center normal-case">
                                        {item}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Glass panel — full width on mobile, fixed on desktop */}
            <div className={`cv2-auth-panel relative w-full flex flex-col justify-between backdrop-blur-[28px] md:border-l px-7 sm:px-10 ${v.panelClass}`}>
                <div className={`md:hidden ${v.mobileLogoWrap}`}>
                    <img src={signupLogo} alt="Claraview" className={`${v.mobileLogoClass} w-auto opacity-90 object-contain`} />
                </div>

                {children}
            </div>
        </div>
    );
};

export default AuthLayout;
